import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCartIcon, StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import useCartStore from '../../store/cartStore';
import { toast } from 'react-hot-toast';

const ProductCard = ({ product }) => {
  const { t, i18n } = useTranslation();
  const addItem = useCartStore((state) => state.addItem);

  const getName = () => {
    const lang = i18n.language;
    if (lang === 'ar' && product.name_ar) return product.name_ar;
    if (lang === 'en' && product.name_en) return product.name_en;
    if (lang === 'ku' && product.name_ku) return product.name_ku;
    return product.name;
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Get store info from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const storeSlug = urlParams.get('store');
    
    addItem(product, storeSlug, product.store_name);
    toast.success(t('common.add_to_cart') || 'تمت الإضافة للسلة');
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating || 0);
    const hasHalfStar = (rating || 0) % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <StarIconSolid key={i} className="w-4 h-4 text-yellow-400" />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <StarIconSolid key={i} className="w-4 h-4 text-yellow-400 opacity-50" />
        );
      } else {
        stars.push(
          <StarIcon key={i} className="w-4 h-4 text-gray-300" />
        );
      }
    }
    return stars;
  };

  const isAvailable = product.is_available && product.stock_quantity > 0;

  return (
    <div className="group bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
      <Link to={`/products/${product.slug}`} className="block">
        <div className="aspect-square bg-gray-100 relative overflow-hidden">
          {product.images && product.images.length > 0 ? (
            <img
              src={product.images[0]}
              alt={getName()}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gradient-to-br from-gray-50 to-gray-100">
              <ShoppingCartIcon className="w-16 h-16 opacity-30" />
            </div>
          )}
          
          {!isAvailable && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <span className="bg-red-500 text-white px-4 py-2 rounded-full font-bold">
                غير متوفر
              </span>
            </div>
          )}
          
          {product.smart_rating > 0 && (
            <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1">
              <StarIconSolid className="w-4 h-4 text-yellow-400" />
              <span className="text-sm font-semibold">{product.smart_rating.toFixed(1)}</span>
            </div>
          )}
        </div>
      </Link>
      
      <div className="p-4">
        <Link to={`/products/${product.slug}`}>
          <h3 className="font-bold text-lg mb-2 text-gray-900 hover:text-blue-600 transition-colors line-clamp-2">
            {getName()}
          </h3>
        </Link>
        
        {product.smart_rating > 0 && (
          <div className="flex items-center gap-1 mb-2">
            {renderStars(product.smart_rating)}
            <span className="text-sm text-gray-500 mr-1">
              ({product.smart_rating.toFixed(1)})
            </span>
          </div>
        )}
        
        <div className="flex items-center justify-between mb-3">
          <div>
            <span className="text-2xl font-bold text-blue-600">
              {product.price?.toLocaleString() || '0'} د.ع
            </span>
            {product.original_price && product.original_price > product.price && (
              <span className="text-sm text-gray-400 line-through mr-2">
                {product.original_price.toLocaleString()} د.ع
              </span>
            )}
          </div>
        </div>
        
        <button
          onClick={handleAddToCart}
          disabled={!isAvailable}
          className={`w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
            isAvailable
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg transform hover:scale-105'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          <ShoppingCartIcon className="w-5 h-5" />
          {isAvailable ? (t('common.add_to_cart') || 'أضف للسلة') : (t('product.out_of_stock') || 'غير متوفر')}
        </button>
      </div>
    </div>
  );
};

export default ProductCard;
